import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { MessageSquare, User, Loader2, Sparkles, Clock, ChevronDown, GitBranch } from 'lucide-react';
import { tplMeta } from './templateMeta';

const FONT_H = { fontFamily: '"Cabinet Grotesk", system-ui, sans-serif' };
const FONT_B = { fontFamily: 'Manrope, system-ui, sans-serif' };

const to12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, '0')} ${ap}`;
};

const timingLabel = (timing) => {
  if (!timing || !Object.keys(timing).length) return null;
  const bits = [];
  if (timing.delay_hours != null) bits.push(timing.delay_hours >= 24 && timing.delay_hours % 24 === 0
    ? `${timing.delay_hours / 24} day${timing.delay_hours / 24 > 1 ? 's' : ''} after`
    : `${timing.delay_hours}h after`);
  if (timing.days_before != null) bits.push(`${timing.days_before} day${timing.days_before > 1 ? 's' : ''} before event`);
  if (timing.days_after != null) bits.push(`${timing.days_after} day${timing.days_after > 1 ? 's' : ''} after event`);
  if (timing.send_time) bits.push(`at ${to12(timing.send_time)}`);
  return bits.join(' · ');
};

const TIMING_FIELD_LABELS = {
  delay_hours: 'Hours to wait', days_before: 'Days before event', days_after: 'Days after event', send_time: 'Send at',
};

function Toggle({ on, onChange, testId }) {
  return (
    <button onClick={onChange}
            className={`relative w-[52px] h-8 rounded-full transition-colors flex-shrink-0 ${on ? 'bg-white' : 'bg-white/10'}`}
            data-testid={testId} aria-label={on ? 'Disable' : 'Enable'}>
      <span className={`absolute top-1 w-6 h-6 rounded-full transition-transform ${on ? 'bg-black translate-x-[22px]' : 'bg-zinc-500 translate-x-1'}`} />
    </button>
  );
}

function WorkflowRow({ wf, onToggle, onSave, onSaveTiming }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(wf.template);
  const [timing, setTiming] = useState(wf.timing || {});
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const hasTiming = Object.keys(wf.default_timing || {}).length > 0;
  const timingDirty = JSON.stringify(timing) !== JSON.stringify(wf.timing || {});

  const save = async () => {
    setSaving(true);
    await onSave(wf.key, text);
    setSaving(false);
  };

  const saveTiming = async () => {
    setSaving(true);
    await onSaveTiming(wf.key, timing);
    setSaving(false);
  };

  const previewDigest = async () => {
    setSaving(true);
    try {
      const res = await api.post('/api/admin/digest/send', { dry_run: true });
      setPreview(res.message);
    } catch (e) { toast.error('Preview failed'); }
    setSaving(false);
  };

  return (
    <div className="bg-[#111111] border border-white/5 rounded-2xl overflow-hidden" data-testid={`workflow-${wf.key}`}>
      <div className="flex items-center gap-4 px-5 py-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${tplMeta(wf.key).bg} ${tplMeta(wf.key).text}`}>
          {wf.audience === 'admin' ? <User className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
        </div>
        <button className="min-w-0 flex-1 text-left" onClick={() => setOpen(!open)} data-testid={`workflow-expand-${wf.key}`}>
          <p className="text-white text-[15px] font-bold tracking-tight" style={FONT_H}>
            {wf.name}
            {wf.is_customized && <span className="ml-2 text-[9px] uppercase tracking-wider text-blue-400 font-bold">edited</span>}
          </p>
          <p className="text-zinc-600 text-xs mt-0.5 leading-snug" style={FONT_B}>{wf.trigger}</p>
          {hasTiming && (
            <p className="text-zinc-400 text-[11px] mt-1 flex items-center gap-1 font-bold" style={FONT_B} data-testid={`workflow-timing-${wf.key}`}>
              <Clock className="w-3 h-3 text-zinc-600" /> {timingLabel(wf.timing)}
            </p>
          )}
        </button>
        <Toggle on={wf.enabled} onChange={() => onToggle(wf.key, !wf.enabled)} testId={`workflow-toggle-${wf.key}`} />
      </div>

      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-white/5" data-testid={`workflow-editor-${wf.key}`}>
          {hasTiming && (
            <div className="pt-4 pb-1" data-testid={`workflow-timing-editor-${wf.key}`}>
              <p className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold mb-2.5" style={FONT_B}>When it sends</p>
              <div className="flex flex-wrap items-end gap-3">
                {Object.keys(wf.default_timing).map(k => (
                  <div key={k}>
                    <span className="text-zinc-500 text-[11px] block mb-1.5 font-bold" style={FONT_B}>{TIMING_FIELD_LABELS[k] || k}</span>
                    {k === 'send_time' ? (
                      <input type="time" value={timing[k] || ''} onChange={(e) => setTiming(t => ({ ...t, [k]: e.target.value }))}
                             className="bg-white/[0.04] border border-white/10 text-white text-sm px-3 py-2.5 rounded-xl focus:outline-none focus:border-white/30 [color-scheme:dark]"
                             data-testid={`timing-${wf.key}-${k}`} />
                    ) : (
                      <input type="number" min="1" value={timing[k] ?? ''} onChange={(e) => setTiming(t => ({ ...t, [k]: parseInt(e.target.value) || 1 }))}
                             className="w-24 bg-white/[0.04] border border-white/10 text-white text-sm px-3 py-2.5 rounded-xl focus:outline-none focus:border-white/30"
                             data-testid={`timing-${wf.key}-${k}`} />
                    )}
                  </div>
                ))}
                {timingDirty && (
                  <button onClick={saveTiming} disabled={saving}
                          className="px-5 py-2.5 rounded-full bg-white text-black text-xs font-bold active:scale-95 disabled:opacity-40 transition-transform"
                          style={FONT_B} data-testid={`timing-save-${wf.key}`}>
                    {saving ? 'Saving…' : 'Save timing'}
                  </button>
                )}
              </div>
            </div>
          )}
          {wf.key === 'daily_digest' ? (
            <div className="pt-4">
              <button onClick={previewDigest} disabled={saving}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-white text-[13px] font-bold active:scale-95 disabled:opacity-40 transition-transform"
                      style={FONT_B} data-testid="digest-preview-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Preview today's brief
              </button>
              {preview && <pre className="text-zinc-400 text-xs leading-relaxed mt-4 whitespace-pre-wrap font-sans" style={FONT_B} data-testid="digest-preview-text">{preview}</pre>}
            </div>
          ) : (
            <div className="pt-4">
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5}
                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-[13px] text-white leading-relaxed focus:outline-none focus:border-white/30 resize-none"
                        style={FONT_B} data-testid={`workflow-template-${wf.key}`} />
              <div className="flex items-center gap-2.5 mt-3">
                <button onClick={save} disabled={saving || text === wf.template}
                        className="px-6 py-2.5 rounded-full bg-white text-black text-[13px] font-bold active:scale-95 disabled:opacity-30 transition-transform"
                        style={FONT_B} data-testid={`workflow-save-${wf.key}`}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                {wf.is_customized && (
                  <button onClick={() => { setText(wf.default_template); onSave(wf.key, ''); }}
                          className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-zinc-400 text-[13px] font-bold active:scale-95 transition-transform"
                          style={FONT_B} data-testid={`workflow-reset-${wf.key}`}>
                    Reset to default
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const AutomationsView = () => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { setData(await api.get('/api/admin/workflows')); }
    catch (err) { console.error('Failed to load workflows', err); toast.error('Failed to load automations'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (key, enabled) => {
    setData(d => ({ ...d, workflows: d.workflows.map(w => w.key === key ? { ...w, enabled } : w) }));
    try {
      await api.put(`/api/admin/workflows/${key}`, { enabled });
      toast.success(enabled ? 'Automation on' : 'Automation off');
    } catch (e) { toast.error('Failed to update'); load(); }
  };

  const saveTemplate = async (key, template) => {
    try {
      await api.put(`/api/admin/workflows/${key}`, { template });
      toast.success(template === '' ? 'Reset to default' : 'Saved');
      await load();
    } catch (e) { toast.error('Failed to save'); }
  };

  const saveTiming = async (key, timing) => {
    try {
      await api.put(`/api/admin/workflows/${key}`, { timing });
      toast.success('Timing saved');
      await load();
    } catch (e) { toast.error(e.message || 'Failed to save timing'); }
  };

  if (!data) return <p className="text-zinc-600 text-sm py-16 text-center" style={FONT_B}>Loading…</p>;

  return (
    <div data-testid="automations-view">
      {!data.client_notify_enabled && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl px-5 py-4 mb-6" data-testid="automations-test-mode-banner">
          <p className="text-blue-300 text-[13px] font-bold" style={FONT_B}>Test mode</p>
          <p className="text-blue-300/60 text-xs mt-1 leading-relaxed" style={FONT_B}>
            Client texts are composed and logged (see Inbox &amp; Today) but not sent — GHL still handles sending. Flip the switch at go-live.
          </p>
        </div>
      )}

      <HowItMoves />

      <p className="text-zinc-500 text-[13px] leading-relaxed mb-6" style={FONT_B}>
        Pre-built automations that run on their own. Toggle any off, tap one to edit its message or <span className="text-zinc-300">change when it sends</span>.
        Placeholders like <span className="text-zinc-300">{'{first}'}</span>, <span className="text-zinc-300">{'{event_date}'}</span>, <span className="text-zinc-300">{'{amount}'}</span> fill in automatically.
      </p>

      <div className="space-y-3">
        {data.workflows.map(wf => (
          <WorkflowRow key={wf.key} wf={wf} onToggle={toggle} onSave={saveTemplate} onSaveTiming={saveTiming} />
        ))}
      </div>
    </div>
  );
};

const STAGE_FLOW = [
  ['New Inquiry', 'Website form, DM or a lead you add. Welcome text fires. You review & build the invoice.', 'bg-blue-400'],
  ['Invoice Sent', 'Send it from the lead card — or drag here on the Pipeline and it offers to send for you.', 'bg-white'],
  ['Deposit Paid', 'Automatic the moment their Stripe deposit lands. Receipt + agreement link go out.', 'bg-orange-400'],
  ['Paid in Full', 'Automatic when the balance is paid. Balance-due reminders below chase it for you.', 'bg-emerald-400'],
  ['Completed', 'Mark it after the event — the review request below goes out on your schedule.', 'bg-zinc-500'],
];

function HowItMoves() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#111111] border border-white/5 rounded-2xl overflow-hidden mb-6" data-testid="how-it-moves">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-4 px-5 py-4 text-left" data-testid="how-it-moves-toggle">
        <div className="w-10 h-10 rounded-full bg-white/5 text-zinc-300 flex items-center justify-center flex-shrink-0">
          <GitBranch className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <p className="text-white text-[15px] font-bold tracking-tight" style={FONT_H}>How leads move through the pipeline</p>
          <p className="text-zinc-600 text-xs mt-0.5" style={FONT_B}>What each stage means & what fires automatically</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-white/5">
          <div className="space-y-0 pt-4">
            {STAGE_FLOW.map(([name, desc, dot], i) => (
              <div key={name} className="flex gap-3.5">
                <div className="flex flex-col items-center">
                  <span className={`w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0 ${dot}`} />
                  {i < STAGE_FLOW.length - 1 && <span className="w-px flex-1 bg-white/10 my-1" />}
                </div>
                <div className="pb-5 min-w-0">
                  <p className="text-white text-[13px] font-bold" style={FONT_B}>{name}</p>
                  <p className="text-zinc-500 text-xs leading-relaxed mt-0.5" style={FONT_B}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
